import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import WebApp from "@twa-dev/sdk";
import { fetchLessonVideos, saveLessonPosition } from "../../utils/api";
import { Banner, Icon } from "../common/ui";

const IFRAME_ALLOW = "accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen;";
const SDK_SRC = "https://embed.cloudflarestream.com/embed/sdk.latest.js";

type Vid = { url: string; aspect?: number | null; title?: string };

// Load the Cloudflare Stream player SDK once. Resolves to window.Stream (or null
// if it can't load — resume still works via the ?startTime= URL param below).
let sdkPromise: Promise<any> | null = null;
function loadStreamSdk(): Promise<any> {
  const w = window as any;
  if (w.Stream) return Promise.resolve(w.Stream);
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve) => {
      const s = document.createElement("script");
      s.src = SDK_SRC;
      s.async = true;
      s.onload = () => resolve((window as any).Stream || null);
      s.onerror = () => resolve(null);
      document.head.appendChild(s);
    });
  }
  return sdkPromise;
}

/** One Cloudflare player with a portaled fullscreen overlay (works in Telegram).
 *  When `onProgress` is given, it reports the current timecode so the lesson can
 *  be resumed later; `startAt` seeks the player to a saved position on load. */
function VideoPlayer({
  url, aspect, startAt = 0, onProgress,
}: {
  url: string; aspect?: number | null;
  startAt?: number;
  onProgress?: (position: number, duration?: number) => void;
}) {
  const a = aspect && aspect > 0 ? aspect : 9 / 16;
  const portrait = a < 1;
  const [fs, setFs] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const posRef = useRef(Math.max(0, Math.floor(startAt)));
  const durRef = useRef<number | undefined>(undefined);
  const lastSavedRef = useRef(-1);

  // Freeze the src per fullscreen state so a live timeupdate never reloads the
  // iframe; recompute on toggle to hand off the current position to the new one.
  const src = useMemo(() => {
    const start = Math.max(0, Math.floor(startAt), Math.floor(posRef.current));
    if (!start) return url;
    return `${url}${url.includes("?") ? "&" : "?"}startTime=${start}s`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, fs, startAt]);

  useEffect(() => {
    if (!fs) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    try { (WebApp as unknown as { requestFullscreen?: () => void }).requestFullscreen?.(); } catch { /* old client */ }
    return () => {
      document.body.style.overflow = prev;
      try { (WebApp as unknown as { exitFullscreen?: () => void }).exitFullscreen?.(); } catch { /* */ }
    };
  }, [fs]);

  // Attach the Stream SDK to whichever iframe is currently mounted (inline XOR
  // fullscreen) to read the timecode and save it periodically.
  useEffect(() => {
    if (!onProgress) return;
    let player: any = null;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const report = () => {
      const pos = Math.floor(posRef.current);
      if (pos < 1 || pos === lastSavedRef.current) return;
      lastSavedRef.current = pos;
      onProgress(pos, durRef.current ? Math.floor(durRef.current) : undefined);
    };
    const onTime = () => {
      if (player && typeof player.currentTime === "number") posRef.current = player.currentTime;
      if (player && player.duration) durRef.current = player.duration;
    };

    loadStreamSdk().then((Stream) => {
      if (cancelled || !Stream || !iframeRef.current) return;
      try {
        player = Stream(iframeRef.current);
        player.addEventListener("timeupdate", onTime);
        player.addEventListener("pause", report);
        player.addEventListener("ended", report);
      } catch { /* SDK attach failed — resume via startTime still works */ }
      timer = setInterval(report, 10000);
    });

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      report();  // save on unmount / fullscreen swap
      try {
        player?.removeEventListener?.("timeupdate", onTime);
        player?.removeEventListener?.("pause", report);
        player?.removeEventListener?.("ended", report);
      } catch { /* */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fs, onProgress]);

  return (
    <div style={{
      position: "relative", width: "100%", maxWidth: portrait ? 380 : 720, margin: "0 auto",
      aspectRatio: String(a), maxHeight: "72vh", borderRadius: 12, overflow: "hidden", background: "#000",
    }}>
      {!fs && (
        <iframe
          ref={iframeRef}
          src={src} title="lesson video" loading="lazy" allow={IFRAME_ALLOW} allowFullScreen
          style={{ border: 0, position: "absolute", inset: 0, width: "100%", height: "100%" }}
        />
      )}
      <button
        onClick={() => setFs(true)} aria-label="Fullscreen"
        style={{
          position: "absolute", top: 8, right: 8, zIndex: 2, width: 40, height: 40, borderRadius: 11,
          border: "none", cursor: "pointer", background: "rgba(0,0,0,0.55)", color: "#fff",
          display: "grid", placeItems: "center", backdropFilter: "blur(4px)",
        }}
      >
        <Icon name="maximize" size={20} />
      </button>

      {fs && createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 4000, background: "#000" }}>
          <iframe
            ref={iframeRef}
            src={src} title="lesson video fullscreen" allow={IFRAME_ALLOW} allowFullScreen
            style={{ border: 0, position: "absolute", inset: 0, width: "100%", height: "100%" }}
          />
          <button
            onClick={() => setFs(false)} aria-label="Close"
            style={{
              position: "fixed", top: 14, right: 14, zIndex: 4001, width: 42, height: 42, borderRadius: 12,
              border: "none", cursor: "pointer", background: "rgba(255,255,255,0.16)", color: "#fff",
              display: "grid", placeItems: "center", backdropFilter: "blur(6px)",
            }}
          >
            <Icon name="x" size={22} />
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default function LessonVideo({
  lessonId, isRu, token, startPosition = 0,
}: {
  lessonId: string; isRu: boolean; token: string; startPosition?: number;
}) {
  const { t } = useTranslation();
  const [videos, setVideos] = useState<Vid[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    fetchLessonVideos(lessonId, isRu ? "ru" : "en", token)
      .then((r) => { if (active) setVideos(r.videos || []); })
      .catch((e) => { if (active) setErr(e instanceof Error ? e.message : t("common.error")); });
    return () => { active = false; };
  }, [lessonId, isRu, token, t]);

  if (err) return <div style={{ marginBottom: 16 }}><Banner kind="error">{err}</Banner></div>;
  if (!videos) {
    return (
      <div style={{ display: "grid", placeItems: "center", padding: 20, marginBottom: 16 }}>
        <div className="spinner spinner-amber" style={{ width: 28, height: 28, borderWidth: 3 }} />
      </div>
    );
  }

  const multi = videos.length > 1;
  // Resume + progress-tracking apply to the primary (first) video of the lesson.
  const saveProgress = (position: number, duration?: number) =>
    saveLessonPosition(lessonId, position, duration, token);

  return (
    <div className="stack" style={{ "--gap": "18px", marginBottom: 16 } as React.CSSProperties}>
      {videos.map((v, i) => (
        <div key={i}>
          {v.title && (
            <div className="t-sm" style={{ fontWeight: 700, marginBottom: 8, color: "var(--violet-text)" }}>
              {multi ? `${i + 1}. ` : ""}{v.title}
            </div>
          )}
          <VideoPlayer
            url={v.url}
            aspect={v.aspect}
            startAt={i === 0 ? startPosition : 0}
            onProgress={i === 0 ? saveProgress : undefined}
          />
        </div>
      ))}
    </div>
  );
}
